
import React, { useState } from 'react';
import type { GridRow, GridColumn, RowAggregationMode, RowType } from '../types/gridTypes';
import { IconX, IconSettings } from './icons';

interface RowConfigModalProps {
  row: GridRow;
  columns: GridColumn[];
  onSave: (updatedRow: GridRow) => void;
  onClose: () => void;
}

export const RowConfigModal: React.FC<RowConfigModalProps> = ({ row, columns, onSave, onClose }) => {
  const [rowType, setRowType] = useState<RowType>(row.rowType || 'task');
  const [aggregationEnabled, setAggregationEnabled] = useState<boolean>(row.aggregationConfig?.enabled || false);
  const [perColumn, setPerColumn] = useState<Record<string, RowAggregationMode>>(
    row.aggregationConfig?.perColumn || {}
  );

  const handleColumnModeChange = (colId: string, mode: RowAggregationMode) => {
    setPerColumn(prev => ({ ...prev, [colId]: mode }));
  };

  const handleSave = () => {
    const updatedRow: GridRow = {
      ...row,
      rowType,
      aggregationConfig: {
        enabled: aggregationEnabled,
        perColumn: aggregationEnabled ? perColumn : undefined
      }
    };
    onSave(updatedRow);
    onClose();
  };

  const aggregatableColumns = columns.filter(c =>
    ['number', 'currency', 'percent'].includes(c.type) ||
    (c.type === 'formula' && ['numeric_metric', 'money', 'percentage'].includes(c.semanticType || ''))
  );

  const otherColumns = columns.filter(c => !aggregatableColumns.includes(c));

  return (
    <div className="modal-overlay fixed inset-0 flex items-center justify-center backdrop-blur-sm">
      <div className="modal-container rounded-lg shadow-2xl w-[500px] max-w-[90vw] flex flex-col max-h-[85vh]">
        <div className="modal-header flex items-center justify-between p-4 border-b">
          <h3 className="modal-title font-bold flex items-center gap-2">
            <IconSettings className="w-5 h-5 modal-icon" />
            Row Configuration
          </h3>
          <button onClick={onClose} className="modal-close-btn">
            <IconX className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-content flex-1 overflow-y-auto p-6 space-y-6">
          {/* Row Type Selection */}
          <div className="space-y-2">
            <label className="modal-label block text-sm font-bold">Row Type</label>
            <div className="grid grid-cols-2 gap-2">
                {['task', 'group', 'subtotal', 'total', 'milestone'].map(type => (
                    <label key={type} className={`modal-radio-option flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${rowType === type ? 'selected' : ''}`}>
                        <input
                            type="radio"
                            name="rowType"
                            value={type}
                            checked={rowType === type}
                            onChange={() => setRowType(type as RowType)}
                            className="modal-radio"
                        />
                        <span className="capitalize text-sm font-medium">{type}</span>
                    </label>
                ))}
            </div>
            <p className="modal-hint text-xs mt-1">
               Affects visual styling and how this row is treated in sheet summaries.
            </p>
          </div>

          <hr className="modal-divider" />

          {/* Aggregation Config */}
          <div className="space-y-4">
             <div className="flex items-center justify-between">
                 <div>
                    <label className="modal-label block text-sm font-bold">Aggregation</label>
                    <p className="modal-hint text-xs">Calculate values from descendant rows</p>
                 </div>
                 <label className="modal-toggle relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={aggregationEnabled} onChange={e => setAggregationEnabled(e.target.checked)} className="sr-only peer" />
                    <div className="toggle-track w-11 h-6 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                 </label>
             </div>

             {aggregationEnabled && (
                 <div className="modal-config-panel rounded-md p-4 space-y-3">
                     <div className="modal-config-title text-xs font-semibold uppercase tracking-wider mb-2">Configure Columns</div>

                     {aggregatableColumns.length === 0 && (
                         <div className="modal-empty-text text-sm italic">No numeric columns available to aggregate.</div>
                     )}

                     {aggregatableColumns.map(col => (
                         <div key={col.id} className="flex items-center justify-between">
                             <span className="modal-column-label text-sm font-medium">{col.label}</span>
                             <select
                                value={perColumn[col.id] || 'none'}
                                onChange={e => handleColumnModeChange(col.id, e.target.value as RowAggregationMode)}
                                className="modal-select text-sm rounded border px-2 py-1 outline-none"
                             >
                                 <option value="none">Manual Entry</option>
                                 <option value="sum">Sum</option>
                                 <option value="avg">Average</option>
                                 <option value="min">Min</option>
                                 <option value="max">Max</option>
                                 <option value="count">Count</option>
                             </select>
                         </div>
                     ))}

                     {/* Simple Count option for non-numeric columns? Not requested but good UX */}
                     {otherColumns.length > 0 && (
                         <details className="pt-2">
                             <summary className="modal-link text-xs cursor-pointer hover:underline">Show other columns</summary>
                             <div className="mt-2 space-y-3">
                                 {otherColumns.map(col => (
                                     <div key={col.id} className="flex items-center justify-between">
                                         <span className="modal-column-label-muted text-sm">{col.label}</span>
                                         <select
                                            value={perColumn[col.id] || 'none'}
                                            onChange={e => handleColumnModeChange(col.id, e.target.value as RowAggregationMode)}
                                            className="modal-select text-sm rounded border px-2 py-1 outline-none"
                                         >
                                             <option value="none">Manual Entry</option>
                                             <option value="count">Count</option>
                                         </select>
                                     </div>
                                 ))}
                             </div>
                         </details>
                     )}
                 </div>
             )}
          </div>
        </div>

        <div className="modal-footer p-4 border-t flex justify-end gap-2 rounded-b-lg">
          <button onClick={onClose} className="modal-btn-secondary px-4 py-2 text-sm rounded font-medium">Cancel</button>
          <button onClick={handleSave} className="modal-btn-primary px-4 py-2 text-sm rounded font-medium shadow-sm">Save Configuration</button>
        </div>
      </div>
    </div>
  );
};
